import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Ticket, Plus, Trash2, ToggleLeft, ToggleRight, Copy, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscountsPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(10);
  const [validDays, setValidDays] = useState(30);
  const [maxUses, setMaxUses] = useState(1);
  const [note, setNote] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/discounts`, { headers });
      setCodes(res.data || []);
    } catch {
      toast.error('Fehler beim Laden der Rabattcodes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/discounts`, {
        discount_percentage: discountPercent,
        valid_days: validDays,
        max_uses: maxUses,
        note: note || null
      }, { headers });
      toast.success('Rabattcode erstellt');
      setNote('');
      fetchCodes();
    } catch {
      toast.error('Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (codeId) => {
    try {
      const res = await axios.put(`${API}/discounts/${codeId}/toggle`, {}, { headers });
      setCodes(prev => prev.map(c => c.id === codeId ? { ...c, is_active: res.data.is_active } : c));
      toast.success(res.data.is_active ? 'Code aktiviert' : 'Code deaktiviert');
    } catch {
      toast.error('Fehler beim Umschalten');
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Code wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/discounts/${codeId}`, { headers });
      setCodes(prev => prev.filter(c => c.id !== codeId));
      toast.success('Code gelöscht');
    } catch {
      toast.error('Fehler beim Löschen');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} kopiert`);
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    try {
      return new Date(expiresAt) < new Date();
    } catch { return false; }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="max-w-6xl space-y-6" data-testid="discounts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="h-6 w-6 text-purple-600" />
            Rabattcodes
          </h1>
          <p className="text-gray-600 mt-1">Rabattcodes erstellen und verwalten</p>
        </div>
        <Button variant="outline" onClick={fetchCodes} disabled={loading} data-testid="discounts-refresh-btn">
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Neuen Code erstellen</CardTitle>
          <CardDescription>Generiert einen einzigartigen Rabattcode</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <Label>Rabatt (%)</Label>
              <Select value={String(discountPercent)} onValueChange={(v) => setDiscountPercent(Number(v))}>
                <SelectTrigger data-testid="discount-percent-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 15, 20, 25, 30, 50].map(v => (
                    <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Gültig (Tage)</Label>
              <Input
                type="number"
                min="1"
                value={validDays}
                onChange={(e) => setValidDays(parseInt(e.target.value || '30', 10))}
                data-testid="discount-valid-days-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Max. Einlösungen</Label>
              <Input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(parseInt(e.target.value || '1', 10))}
                data-testid="discount-max-uses-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notiz (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="z.B. Re-Engagement 60d"
                data-testid="discount-note-input"
              />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={creating} data-testid="discount-create-btn">
            <Plus size={16} className="mr-2" />
            {creating ? 'Erstellt...' : 'Code generieren'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alle Codes ({codes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Laden...</p>
          ) : codes.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Keine Rabattcodes vorhanden</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="discounts-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Rabatt</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Einlösungen</TableHead>
                    <TableHead>Gültig bis</TableHead>
                    <TableHead>Notiz</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((c) => {
                    const expired = isExpired(c.expires_at);
                    const maxed = c.max_uses > 0 && c.uses >= c.max_uses;
                    return (
                      <TableRow key={c.id} data-testid={`discount-row-${c.id}`}>
                        <TableCell>
                          <button
                            onClick={() => copyCode(c.code)}
                            className="font-mono font-bold text-purple-700 hover:text-purple-900 flex items-center gap-1"
                            data-testid={`discount-code-${c.id}`}
                          >
                            {c.code} <Copy size={12} />
                          </button>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {c.discount_percentage}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {expired ? (
                            <Badge variant="outline" className="bg-gray-100 text-gray-500">Abgelaufen</Badge>
                          ) : maxed ? (
                            <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Aufgebraucht</Badge>
                          ) : c.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Aktiv</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-600">Deaktiviert</Badge>
                          )}
                        </TableCell>
                        <TableCell>{c.uses}/{c.max_uses}</TableCell>
                        <TableCell>{formatDate(c.expires_at)}</TableCell>
                        <TableCell className="max-w-[150px] truncate text-sm text-gray-500">{c.note || '-'}</TableCell>
                        <TableCell>{formatDate(c.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggle(c.id)}
                              data-testid={`discount-toggle-${c.id}`}
                            >
                              {c.is_active ? <ToggleRight size={16} className="text-green-600" /> : <ToggleLeft size={16} className="text-gray-400" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(c.id)}
                              data-testid={`discount-delete-${c.id}`}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DiscountsPage;
